/**
 * PostureService 兼容性包装类
 * 提供旧 API 兼容的接口，内部使用新的 protobuf-ts 生成的客户端
 */
import { PostureServiceClient } from "../../api/algorithm/posture/posture.client";
import { PostureService as PostureServiceType } from "../../api/algorithm/posture/posture";
import { HttpRpcTransport } from "./HttpRpcTransport";
import { HttpResponse } from "../../assets/javascripts/http";
import { StringValue } from "../../api/google/protobuf/wrappers";

/**
 * 将 StringValue 转换为普通字符串
 */
function unwrapStringValue(value: StringValue | undefined): string | undefined {
  return value?.value;
}

/**
 * 将普通字符串转换为 StringValue
 */
function wrapStringValue(value: string | undefined): StringValue | undefined {
  return value ? { value } : undefined;
}

/**
 * 递归解包装所有 StringValue 类型
 */
function unwrapResponse(obj: any): any {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => unwrapResponse(item));
  }

  const result: any = {};
  for (const key in obj) {
    const value = obj[key];
    if (value && typeof value === 'object' && 'value' in value && Object.keys(value).length === 1) {
      // 这是一个包装类型（StringValue, Int32Value 等）
      result[key] = value.value;
    } else if (value && typeof value === 'object') {
      result[key] = unwrapResponse(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * PostureService 包装类
 * 保持与旧代码相同的构造函数签名和方法调用方式
 */
export class PostureService {
  private client: PostureServiceClient;

  constructor(baseUrl: string, postFn: (url: string, headers: any, body: any) => Promise<HttpResponse>) {
    const transport = new HttpRpcTransport(baseUrl, postFn);
    this.client = new PostureServiceClient(transport);
  }

  // 包装所有方法，保持旧的 API 调用方式
  async getPostureReport(request: any, accountId?: string) {
    const response = await this.client.getPostureReport(request, {
      meta: accountId ? { "xz-account-id": accountId } : undefined,
    }).response;
    return unwrapResponse(response);
  }

  async getPostureReportList(request: any) {
    const response = await this.client.getPostureReportHeaderList(request).response;
    // 转换字段名：postureReportHeaders -> postureReportList
    const unwrapped = unwrapResponse(response);
    return {
      ...unwrapped,
      postureReportList: unwrapped.postureReportHeaders || []
    };
  }

  async getMuscleState(request: any) {
    const response = await this.client.getMuscleState(request).response;
    return unwrapResponse(response);
  }

  async updatePostureReportCoordinate(request: any) {
    const response = await this.client.updatePostureReportCoordinate(request).response;
    return unwrapResponse(response);
  }

  async getLicenseInfoByAccountIds(request: any) {
    const response = await this.client.getLicenseInfoByAccountIds(request).response;
    return unwrapResponse(response);
  }
}
